import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Loader2, MoreHorizontal, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { useProductCategoriesTableFilteringStore } from '~/stores/product-categories-table-filtering-store';
import Content from '../components/layout/Content';
import { toast } from 'sonner';

type ProductCategory = {
    id: number;
    name: string;
    parentId: number | null;
    newTreatments: { id: string; name: string }[];
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
};

const productCategoryColumns: ColumnDef<ProductCategory>[] = [
    {
        accessorKey: 'id',
        header: 'ID',
    },
    {
        accessorKey: 'name',
        header: ({ column }) => {
            return (
                <Button
                    variant='ghost'
                    onClick={() => {
                        const currentSort = column.getIsSorted();
                        if (currentSort === 'asc') {
                            column.toggleSorting(true);
                        } else if (currentSort === 'desc') {
                            column.clearSorting();
                        } else {
                            column.toggleSorting(false);
                        }
                    }}
                >
                    Name
                    {column.getIsSorted() === 'asc' ? (
                        <ArrowUp className='ml-2 h-4 w-4' />
                    ) : column.getIsSorted() === 'desc' ? (
                        <ArrowDown className='ml-2 h-4 w-4' />
                    ) : (
                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                    )}
                </Button>
            );
        },
        enableSorting: true,
    },
    {
        id: 'newTreatments',
        header: 'Leistungen',
        cell: ({ row }) => {
            if (row.original.parentId) return null;
            return <Badge variant='secondary'>{row.original.newTreatments?.length ?? 0}</Badge>;
        },
    },
];

interface CategoryFormData {
    name: string;
    parentId: number | null;
}

interface CategorySheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    category: ProductCategory | null;
    onSave: (data: CategoryFormData) => void;
    onDelete: () => void;
    isSaving: boolean;
}

const CategorySheet = ({ open, onOpenChange, category, onSave, onDelete, isSaving }: CategorySheetProps) => {
    const [name, setName] = useState('');
    const [parentId, setParentId] = useState<number | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const { data: parentCategories } = trpc.treatmentCategories.getAllSimple.useQuery({ parentIdOnly: true });

    useEffect(() => {
        if (category) {
            setName(category.name);
            setParentId(category.parentId);
        } else {
            setName('');
            setParentId(null);
        }
    }, [category, open]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (name.trim()) {
            onSave({ name: name.trim(), parentId });
        }
    };

    // Filter out the current category and its treatment from parent options
    const availableParents = parentCategories?.filter(cat => {
        if (!category) return true;
        if (cat.id === category.id) return false;
        return true;
    }) ?? [];

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className='overflow-y-auto'>
                    <form onSubmit={handleSubmit}>
                        <SheetHeader>
                            <SheetTitle>
                                {category ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
                            </SheetTitle>
                            <SheetDescription>
                                {category
                                    ? 'Bearbeiten Sie die Daten der Leistungskategorie.'
                                    : 'Geben Sie die Daten für die neue Leistungskategorie ein.'}
                            </SheetDescription>
                        </SheetHeader>
                        <div className='flex flex-col gap-4 py-4 px-4'>
                            <div className='flex flex-col gap-2'>
                                <Label htmlFor='name'>Name</Label>
                                <Input
                                    id='name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder='Kategoriename eingeben'
                                    autoFocus
                                />
                            </div>
                            {category && category.newTreatments?.length > 0 && (
                                <div className='flex flex-col gap-2'>
                                    <Label>Leistungen</Label>
                                    <div className='flex flex-wrap gap-2'>
                                        {category.newTreatments.map((treatment) => (
                                            <Badge key={treatment.id} variant='outline'>
                                                {treatment.name}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <SheetFooter>
                            {category && (
                                <Button
                                    type='button'
                                    variant='destructive'
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={isSaving}
                                >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Löschen
                                </Button>
                            )}
                            <Button type='submit' disabled={isSaving || !name.trim()}>
                                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                {category ? 'Speichern' : 'Erstellen'}
                            </Button>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Möchten Sie die Kategorie &quot;{category?.name}&quot; wirklich löschen? Diese Aktion
                            kann nicht rückgängig gemacht werden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setShowDeleteDialog(false);
                                onDelete();
                            }}
                            className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

const ProductCategoriesDataTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);

    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useProductCategoriesTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    const [sheetOpen, setSheetOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<ProductCategory | null>(null);

    useEffect(() => {
        if (isInitialized.current) return;
        isInitialized.current = true;

        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        const urlSortField = searchParams.get('sort');
        const urlSortDir = searchParams.get('dir') || 'asc';

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
            }
        } else if (storeSorting.length > 0) {
            newParams.set('sort', storeSorting[0].id);
            newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        }

        const urlPage = searchParams.get('page');
        const urlSize = searchParams.get('size');

        if (urlPage !== null || urlSize !== null) {
            const pageIndex = urlPage !== null ? Math.max(0, parseInt(urlPage, 10) || 0) : storePagination.pageIndex;
            const pageSize = urlSize !== null ? Math.max(1, parseInt(urlSize, 10) || 10) : storePagination.pageSize;
            if (pageIndex !== storePagination.pageIndex || pageSize !== storePagination.pageSize) {
                setPagination({ pageIndex, pageSize });
            }
        } else {
            newParams.set('page', String(storePagination.pageIndex));
            newParams.set('size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        const urlFilters: ColumnFiltersState = [];
        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                urlFilters.push({ id: key.replace('filter_', ''), value });
            }
        }
        if (urlFilters.length > 0) {
            setColumnFilters(urlFilters);
        }

        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams, setSearchParams, storeSorting, storePagination, setSorting, setPagination, setColumnFilters]);

    const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10);
    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter_')) {
            queryFilters[key.replace('filter_', '')] = value;
        }
    }

    const utils = trpc.useUtils();

    const {
        data: categoriesResponse,
        isLoading,
        isFetching,
    } = trpc.treatmentCategories.getAll.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            filters: queryFilters,
            parentIdOnly: true,
        },
        {
            placeholderData: (prev) => prev,
        }
    );

    const createMutation = trpc.treatmentCategories.create.useMutation({
        onSuccess: () => {
            utils.treatmentCategories.getAll.invalidate();
            utils.treatmentCategories.getAllSimple.invalidate();
            toast.success('Kategorie erfolgreich erstellt');
            setSheetOpen(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen der Kategorie', { description: error.message });
        },
    });

    const updateMutation = trpc.treatmentCategories.update.useMutation({
        onSuccess: () => {
            utils.treatmentCategories.getAll.invalidate();
            utils.treatmentCategories.getAllSimple.invalidate();
            toast.success('Kategorie erfolgreich aktualisiert');
            setSheetOpen(false);
        },
        onError: (error) => {
            if (error.message.includes('child') || error.message.includes('parent')) {
                toast.error('Ungültige Kategoriestruktur', {
                    description: 'Eine Kategorie kann nicht ihre eigene Elternkategorie sein.',
                });
            } else {
                toast.error('Fehler beim Aktualisieren der Kategorie', { description: error.message });
            }
        },
    });

    const deleteMutation = trpc.treatmentCategories.delete.useMutation({
        onSuccess: () => {
            utils.treatmentCategories.getAll.invalidate();
            utils.treatmentCategories.getAllSimple.invalidate();
            toast.success('Kategorie erfolgreich gelöscht');
            setSheetOpen(false);
        },
        onError: (error) => {
            if (error.message.includes('treatment')) {
                toast.error('Kategorie kann nicht gelöscht werden', {
                    description: 'Die Kategorie hat noch Leistungen.',
                });
            } else if (error.message.includes('products')) {
                toast.error('Kategorie kann nicht gelöscht werden', {
                    description: 'Der Kategorie sind noch Leistungen zugewiesen.',
                });
            } else {
                toast.error('Fehler beim Löschen der Kategorie', { description: error.message });
            }
        },
    });

    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let changed = false;

        if (storeSorting.length > 0) {
            if (searchParams.get('sort') !== storeSorting[0].id) {
                newParams.set('sort', storeSorting[0].id);
                changed = true;
            }
            const dir = storeSorting[0].desc ? 'desc' : 'asc';
            if (searchParams.get('dir') !== dir) {
                newParams.set('dir', dir);
                changed = true;
            }
        } else {
            if (searchParams.has('sort')) {
                newParams.delete('sort');
                newParams.delete('dir');
                changed = true;
            }
        }

        if (changed) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, searchParams, setSearchParams]);

    useEffect(() => {
        if (!isInitialized.current) return;
        const currentPage = searchParams.get('page');
        const currentSize = searchParams.get('size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('page', storePage);
            newParams.set('size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, searchParams, setSearchParams]);

    // Sync filter state with URL
    useEffect(() => {
        if (!isInitialized.current) return;
        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;
        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));
        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach((f) => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`filter_${id}`);
                filtersChanged = true;
            }
        });
        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                if (value) {
                    newParams.set(`filter_${id}`, value);
                } else {
                    newParams.delete(`filter_${id}`);
                }
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, searchParams, setSearchParams]);

    const table = useReactTable({
        data: (categoriesResponse?.data ?? []) as ProductCategory[],
        columns: productCategoryColumns,
        pageCount: categoriesResponse?.pageCount ?? -1,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleOpenCreate = () => {
        setSelectedCategory(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (category: ProductCategory) => {
        setSelectedCategory(category);
        setSheetOpen(true);
    };

    const handleSave = (data: CategoryFormData) => {
        if (selectedCategory) {
            updateMutation.mutate({
                id: selectedCategory.id,
                name: data.name,
                parentId: data.parentId,
            });
        } else {
            createMutation.mutate({
                name: data.name,
                parentId: data.parentId,
            });
        }
    };

    const handleDelete = () => {
        if (selectedCategory) {
            deleteMutation.mutate({ id: selectedCategory.id });
        }
    };

    const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

    if (isLoading && !categoriesResponse) {
        return (
            <div className='bg-white rounded-lg shadow-sm p-6'>
                <div className='flex items-center justify-between py-4'>
                    <Skeleton className='h-10 w-[250px]' />
                    <Skeleton className='h-10 w-[150px]' />
                </div>
                <div className='rounded-md border border-gray-200'>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {productCategoryColumns.map((_, index) => (
                                    <TableHead key={index}>
                                        <Skeleton className='h-6 w-full' />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: 5 }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {productCategoryColumns.map((_, colIndex) => (
                                        <TableCell key={colIndex}>
                                            <Skeleton className='h-6 w-full' />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        );
    }

    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Nach Name filtern...'
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('name')?.setFilterValue(event.target.value);
                        }}
                        className='max-w-sm'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <Button onClick={handleOpenCreate}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neue Kategorie
                </Button>
            </div>

            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className='font-medium text-gray-700'>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                                <TableHead className='w-[50px]' />
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className='cursor-pointer hover:bg-gray-50 transition-colors'
                                    onClick={() => handleOpenEdit(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                <Button variant='ghost' className='h-8 w-8 p-0'>
                                                    <span className='sr-only'>Menü öffnen</span>
                                                    <MoreHorizontal className='h-4 w-4' />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align='end'>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenEdit(row.original);
                                                    }}
                                                >
                                                    Bearbeiten
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(String(row.original.id));
                                                        toast.success('ID kopiert');
                                                    }}
                                                >
                                                    ID kopieren
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={productCategoryColumns.length + 1}
                                    className='h-24 text-center'
                                >
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Daten werden geladen...
                                        </div>
                                    ) : (
                                        'Keine Kategorien gefunden.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                        className='border border-gray-300 rounded px-2 py-1 text-sm'
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {pageSize} pro Seite
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {table.getState().pagination.pageIndex + 1} von{' '}
                        {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                    >
                        &lt;
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                    >
                        &gt;
                    </Button>
                </div>
            </div>

            <CategorySheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                category={selectedCategory}
                onSave={handleSave}
                onDelete={handleDelete}
                isSaving={isMutating}
            />
        </div>
    );
};

const ProductCategories = () => {
    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-2xl font-bold mb-6'>Leistungskategorien</h1>
                <ProductCategoriesDataTable />
            </div>
        </Content>
    );
};

export default ProductCategories;
